import styled, { css } from "styled-components";

import {
    COLUMN_CARD_HEIGHT,
    COLUMN_ROW_GAP,
    COLUMN_ROW_TRANSITION_MS,
} from "app/pages/sales-intelligence/common-components/column-picker/constants";

const exitStyles = css`
    opacity: 0;
    height: 0px;
    margin-bottom: 0px;
    overflow: hidden;
`;

const initialStyles = css`
    height: ${COLUMN_CARD_HEIGHT}px;
    opacity: 1;
    margin-bottom: ${COLUMN_ROW_GAP}px;
`;

export const FadeContainer = styled.div`
    transition-property: height, opacity, margin-bottom;
    transition-duration: ${COLUMN_ROW_TRANSITION_MS}ms;
    transition-timing-function: ease-in-out;

    ${initialStyles}
    &.fade {
        &-enter {
            ${exitStyles}
        }

        &-enter-active,
        &-exit {
            ${initialStyles}
        }

        &-exit-active {
            ${exitStyles}
        }
    }
`;

/**
 * The left-hand list is virtualised, so its rows are positioned out of flow by an inline
 * `transform` the virtualiser supplies. That is what makes `TransitionGroup` safe here: a row it
 * retains while the exit animation runs stays pinned where it was instead of pushing its successors
 * down, which is what made the list jump.
 *
 * The inherited `margin-bottom` is inert on an absolutely positioned box with `top` and `height`
 * resolved — the gap between rows comes from the virtualiser's row pitch instead.
 *
 * `transition: transform` is what lets the rows below a collapsing one slide up in step with it,
 * and it is gated on the track so it never fires while scrolling repositions rows.
 */
export const VirtualFadeContainer = styled(FadeContainer)`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    /* Deliberately without paint containment: it clips descendants to this box, and the row's hover
       elevation is drawn entirely outside its own border box. The collapse has its own overflow. */
    contain: layout style;

    [data-layout-animation] > & {
        transition-property: height, opacity, margin-bottom, transform;

        @media (prefers-reduced-motion: reduce) {
            transition-property: height, opacity, margin-bottom;
        }
    }
`;

export const StyledDraggableContainer = styled.div<{ isDragging: boolean }>`
    opacity: ${(p) => (p.isDragging ? 0 : 1)};

    &:hover {
        cursor: grab;
    }
`;
